import axios from 'axios';
import AuthenticationUtil from 'utils/AuthenticationUtil';
import { getFullLocale } from 'utils/deprecated/LocaleMapper';
import DiTokenManager from '../auth/DiTokenManager';
import SnackbarMessageAction from '../system/SnackbarMessageAction';
import { isReadonlyMode, isProductSupportMode } from 'system/SystemUtil';
import viewerUserPreferences from 'models/user/ViewerUserPreferences';

const responseInterceptors = [
    function(response) {
        // Do something with response data
        return response;
    },
    function(error) {
        if (error.response && error.response.status === 403 && isReadonlyMode()) {
            // Ignore userpreference error
            if (!error.response.url.startsWith('/userpreference-service')) {
                SnackbarMessageAction.display({ ns: 'settings', transKey: 'read_only_mode_alert_2' });
            }
        }
        if (
            error.response &&
            error.response.status === 401 &&
            (isProductSupportMode() || isReadonlyMode()) &&
            error.response.config.url.startsWith('/modern/it-proxy')
        ) {
            // it-proxy does not have auth in account sharing modes. Ignore 401 errors.
            return;
        }
        // Do something with response error
        if (error.response && error.response.status === 401) {
            // Session is invalid, log user out
            AuthenticationUtil.signOut();
            return;
        }
        return Promise.reject(error);
    }
];

/*
 *   Default instance of axios configured for /modern/proxy calls
 */
const configureAxios = () => {
    const viewerUserPreferencesJson = viewerUserPreferences?.toJSON();
    axios.defaults.baseURL = '/modern/proxy';
    axios.defaults.timeout = 15000;
    axios.defaults.headers.common = {
        ...axios.defaults.headers.common,
        NK: 'NT',
        'X-app-ver': window.URL_BUST_VALUE,
        ...(window.VIEWER_USERPREFERENCES && window.VIEWER_USERPREFERENCES.preferredLocale
            ? { 'X-lang': getFullLocale(window.VIEWER_USERPREFERENCES.preferredLocale) }
            : viewerUserPreferencesJson?.preferredLocale
            ? { 'X-lang': getFullLocale(viewerUserPreferencesJson.preferredLocale) }
            : {})
    };
    axios.interceptors.response.use(...responseInterceptors);
    delete axios.defaults.xsrfHeaderName;
    delete axios.defaults.xsrfCookieName;
};
export default configureAxios;

/*
 *   Interceptors to handle Authoriation header for DI OAuth
 */
const authHeaderInterceptor = async (config) => {
    const token = await DiTokenManager.getToken();

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
};

const interceptorError = (error) => {
    return Promise.reject(error);
};

/*
 *   Interceptors to handle Authoriation header for DI OAuth
 */
const sessionInterceptor = async (config) => {
    if (window.USE_DI_SESSION && window.SESSION_EXPIRES) {
        if (window.SESSION_EXPIRES.expires < Date.now()) {
            // Access token is expired
            if (window.SESSION_EXPIRES.refreshExpires < Date.now()) {
                // Refresh is expired.  Reload the page to have the server try to start a new session.
                window.location.reload();
            } else {
                await DiTokenManager.refreshSession();
            }
        }

        // Add the CSRF header
        config.headers['Connect-Csrf-Token'] = document
            .querySelector("meta[name='csrf-token']")
            ?.getAttribute('content');
    }

    return config;
};

/*
 *   Axios Instance for GC API calls using DI OAuth
 */
let gcApi;
if (window.USE_DI_SESSION && window.SESSION_EXPIRES) {
    gcApi = axios.create({
        baseURL: '/gc-api/'
    });
    gcApi.interceptors.request.use(sessionInterceptor, interceptorError);
} else {
    gcApi = axios.create({
        baseURL: '/',
        headers: { ...axios.defaults.headers, 'DI-Backend': window.DI_BACKENDS && window.DI_BACKENDS.gcapi }
    });
    gcApi.interceptors.request.use(authHeaderInterceptor, interceptorError);
}
gcApi.interceptors.response.use(...responseInterceptors);
export { gcApi };

/*
 *   Axios Instance for GHS API calls using DI OAuth
 */
const ghsApi = axios.create({
    baseURL: `https://${window.DI_BACKENDS && window.DI_BACKENDS.ghs}/`,
    headers: { ...axios.defaults.headers, 'DI-Backend': window.DI_BACKENDS && window.DI_BACKENDS.ghs },
    // indicates whether or not cross-site Access-Control requests should be made using credentials (cookies)
    withCredentials: true
});

ghsApi.interceptors.request.use(authHeaderInterceptor, interceptorError);
export { ghsApi };

/*
 *   Axios Instance for Golf API calls using DI OAuth
 */
const golfApi = axios.create({
    baseURL: '/',
    headers: { ...axios.defaults.headers, 'DI-Backend': window.DI_BACKENDS && window.DI_BACKENDS.golf }
});

golfApi.interceptors.request.use(authHeaderInterceptor, interceptorError);
export { golfApi };

/*
 *   Axios Instance for ATP (Coach) API calls using DI OAuth
 */
const atpApi = axios.create({
    baseURL: '/',
    headers: { ...axios.defaults.headers, 'DI-Backend': window.DI_BACKENDS && window.DI_BACKENDS.atp }
});

atpApi.interceptors.request.use(authHeaderInterceptor, interceptorError);
export { atpApi };

/*
 *   Axios Instance for GCS API calls using DI OAuth
 */
const gcsApi = axios.create({
    baseURL: '/',
    headers: { ...axios.defaults.headers, 'DI-Backend': window.DI_BACKENDS && window.DI_BACKENDS.gcs }
});

gcsApi.interceptors.request.use(authHeaderInterceptor, interceptorError);
export { gcsApi };

/*
 *   Axios Instance for GCS calls
 */
const gcs = axios.create({
    baseURL: '/',
    headers: { ...axios.defaults.headers, 'DI-Backend': window.DI_BACKENDS && window.DI_BACKENDS.gcsalt }
});

gcs.interceptors.request.use(authHeaderInterceptor, interceptorError);
export { gcs };

/*
 *   Axios Instance for OMT API calls using DI OAuth
 */
const omtApi = axios.create({
    baseURL: '/',
    headers: { ...axios.defaults.headers, 'DI-Backend': window.DI_BACKENDS && window.DI_BACKENDS.omt }
});

omtApi.interceptors.request.use(authHeaderInterceptor, interceptorError);
export { omtApi };

/*
 *   Axios Instance for OMT CMS API calls using DI OAuth
 */
const omtCmsApi = axios.create({
    baseURL: '/api-omt-cms'
});

omtCmsApi.interceptors.request.use(authHeaderInterceptor, interceptorError);
export { omtCmsApi };

/*
 *   Axios Instance for Insights API calls using DI OAuth
 */
const insightsApi = axios.create({
    baseURL: '/',
    headers: { ...axios.defaults.headers, 'DI-Backend': window.DI_BACKENDS && window.DI_BACKENDS.insights }
});

insightsApi.interceptors.request.use(authHeaderInterceptor, interceptorError);
export { insightsApi };

/*
 *   Axios Instance for Trails API calls using DI OAuth
 */
let trailsApi;
if (window.USE_DI_SESSION && window.SESSION_EXPIRES) {
    trailsApi = axios.create({
        baseURL: '/didt-api/'
    });
    trailsApi.interceptors.request.use(sessionInterceptor, interceptorError);
} else {
    trailsApi = axios.create({
        baseURL: '/',
        headers: { ...axios.defaults.headers, 'DI-Backend': window.DI_BACKENDS && window.DI_BACKENDS.didt }
    });
    trailsApi.interceptors.request.use(authHeaderInterceptor, interceptorError);
}

trailsApi.interceptors.response.use(...responseInterceptors);
export { trailsApi };

/*
 *   Axios Instance for Web API calls using DI OAuth
 */
let webApi;
if (window.USE_DI_SESSION && window.SESSION_EXPIRES) {
    webApi = axios.create({
        baseURL: '/web-api/'
    });

    webApi.interceptors.request.use(sessionInterceptor, interceptorError);
} else {
    // webApi is new.  The calls to web-data were using gcApi previously
    webApi = gcApi;
}
webApi.interceptors.response.use(...responseInterceptors);
export { webApi };
